import { supabase } from '@/integrations/supabase/client';
import { useAppStore } from '@/store';

// Simple user-agent parser
const parseUserAgent = () => {
  if (typeof window === 'undefined') return { browser: 'unknown', os: 'unknown' };
  const ua = navigator.userAgent;
  let browser = 'unknown';
  let os = 'unknown';

  // OS detection
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS/.test(ua)) os = 'macOS';
  else if (/Linux/.test(ua)) os = 'Linux';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';

  // Browser detection
  if (/Firefox/.test(ua)) browser = 'Firefox';
  else if (/SamsungBrowser/.test(ua)) browser = 'Samsung Internet';
  else if (/Opera|OPR/.test(ua)) browser = 'Opera';
  else if (/Trident/.test(ua)) browser = 'Internet Explorer';
  else if (/Edge/.test(ua)) browser = 'Edge';
  else if (/Chrome/.test(ua)) browser = 'Chrome';
  else if (/Safari/.test(ua)) browser = 'Safari';

  return { browser, os };
};

// Function to get geolocation
const getGeolocation = (): Promise<{ latitude: number; longitude: number } | null> => {
  return new Promise((resolve) => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        () => {
          resolve(null); // User denied or error occurred
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 1000 * 60 * 30, // Use cached location for 30 mins
        }
      );
    } else {
      resolve(null); // Geolocation not supported
    }
  });
};

type EventType = 'page_view' | 'chat_created' | 'message_sent' | 'login' | 'logout';

interface EventPayload {
  [key: string]: any;
}

export const trackEvent = async (eventType: EventType, payload?: EventPayload) => {
  const { allowAnalytics } = useAppStore.getState();
  if (!allowAnalytics) {
    return;
  }

  const { data: { session } } = await supabase.auth.getSession();
  
  // Allow page_view events for guests, but require auth for other events
  if (!session?.user && !['login', 'logout', 'page_view'].includes(eventType)) {
    return;
  }

  const { browser, os } = parseUserAgent();
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const geolocation = await getGeolocation();

  const eventPayload = {
    ...payload,
    path: payload?.path || window.location.pathname,
    pageTitle: payload?.pageTitle || document.title,
    browser,
    os,
    screenWidth,
    screenHeight,
  };

  try {
    // For login/logout/page_view events, we can send without auth header
    const headers: Record<string, string> = {};

    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }

    const { error } = await supabase.functions.invoke('log-analytics', {
      body: {
        eventType,
        payload: eventPayload,
        geolocation,
        userId: session?.user?.id,
        userEmail: session?.user?.email,
      },
    });

    if (error) {
      // Only log analytics errors in development to avoid console spam in production
      if (import.meta.env.DEV) {
        console.error('Error tracking analytics event:', error);
      }
    }
  } catch (error) {
    // Silently handle network/function errors
    if (import.meta.env.DEV) {
      console.warn('Analytics tracking failed:', error);
    }
  }
};